import { Box, Drawer, Link, Stack, Tooltip, Typography } from '@mui/material'
import { DataGrid, type GridColumns, GridOverlay } from '@mui/x-data-grid'
import { matchSorter } from 'match-sorter'
import React, { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { SearchField } from 'components/common/SearchField'
import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import { AgentDetailsDrawerContent } from 'components/configurations/agents/AgentDetailsDrawerContent'
import { AgentsSummaryContainer } from 'components/configurations/agents/AgentsSummaryContainer'
import { DeactivateAgentButton } from 'components/configurations/agents/DeactivateAgentButton'
import { ReactivateAgentButton } from 'components/configurations/agents/ReactivateAgentButton'
import { SEARCH_INPUT_FIELD_WIDTH } from 'components/configurations/configurations-utils'
import { useAgentDetailsDrawerContext } from 'components/configurations/contexts/AgentDetailsDrawerContext'
import Icon from 'components/Icon'
import useAuth from 'hooks/use-auth'
import {
  SEARCH_PARAMS_KEY,
  useDebouncedSearchParams
} from 'hooks/use-debounced-search-params'
import { muiTheme } from 'mui-theme'
import { type Agent, AgentStatus, AgentType } from 'types/api/agents/agents'
import {
  clientsIdActivatePut,
  clientsIdDelete,
  failureFlagsAppsProtectPost,
  failureFlagsAppsUnprotectPost
} from 'utils/endpoints-access'
import { naturalSortBy } from 'utils/list'
import {
  defaultColumnProps,
  defaultGridProps,
  getGridStyles
} from 'utils/mui-components/data-grid-utils'

function getAgentStatusColor(status: AgentStatus, isWarning: boolean) {
  if (status === AgentStatus.ACTIVE && isWarning) {
    return 'warning.main'
  }

  if (status === AgentStatus.ACTIVE) {
    return 'success.main'
  }

  return 'text.primary.main'
}

const AgentListEmptyState: React.FC = () => {
  return (
    <GridOverlay>
      <Typography>No agents found</Typography>
    </GridOverlay>
  )
}

export const AgentsList: React.FC<{
  agents: Agent[]
}> = ({ agents }) => {
  const { hasEndpointAccessForAll } = useAuth()
  const [pageSize, setPageSize] = useState(15)
  const [searchParams] = useSearchParams()
  const searchParamsValue = searchParams.get(SEARCH_PARAMS_KEY) ?? ''
  const [searchValue, setSearchValue] = useDebouncedSearchParams({
    initialSearchValue: searchParamsValue
  })

  const { isOpen, handleRowClick, handleClose, handleOpen } =
    useAgentDetailsDrawerContext()

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
  }

  const hasPrivilegesToEdit = useCallback(
    (agent: Agent) => {
      const isInfrastructureAgent = agent.type === AgentType.INFRASTRUCTURE

      const apiProps = isInfrastructureAgent
        ? [clientsIdActivatePut, clientsIdDelete]
        : [failureFlagsAppsProtectPost, failureFlagsAppsUnprotectPost]

      return hasEndpointAccessForAll(apiProps, true)
    },
    [hasEndpointAccessForAll]
  )

  const isActionsColumnVisible = useMemo(() => {
    return (
      hasEndpointAccessForAll([clientsIdActivatePut, clientsIdDelete], true) ||
      hasEndpointAccessForAll(
        [failureFlagsAppsProtectPost, failureFlagsAppsUnprotectPost],
        true
      )
    )
  }, [hasEndpointAccessForAll])

  const filteredAgents = useMemo(() => {
    if (!searchParamsValue) {
      return agents
    }

    // Split the search terms by space and/or commas to allow multi-term search (e.g., "tag: value", including spaces)
    const terms = searchParamsValue.trim().split(/[\s,]+/)

    return terms.reduceRight(
      (results, term) =>
        matchSorter(results, term, {
          keys: [
            'id',
            // Format the tags into a string that can be matched against the search value
            (item) =>
              Object.entries(item.tags).map(
                ([key, values]) => `${key}:${values.join('')}`
              )
          ],
          sorter: (rankedItems) =>
            // Maintain the initial agents sorting order, alphabetically by "id" within each group:
            // 1. Active agents with warnings
            // 2. Active agents without warnings
            // 3. Deactivated agents
            // 4. Idle agents
            naturalSortBy(rankedItems, [
              ({ item }) =>
                item.status === AgentStatus.ACTIVE && item.warningCount > 0
                  ? 0
                  : 1,
              ({ item }) => (item.status === AgentStatus.ACTIVE ? 0 : 1),
              ({ item }) => (item.status === AgentStatus.DEACTIVATED ? 0 : 1),
              ({ item }) => item.id
            ]),
          threshold: matchSorter.rankings.CONTAINS
        }),
      agents
    )
  }, [agents, searchParamsValue])

  const columns: GridColumns<Agent> = [
    {
      ...defaultColumnProps,
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: ({ row }) => {
        const { status, warningCount } = row

        const isWarning = status === AgentStatus.ACTIVE && warningCount > 0

        return (
          <Tooltip
            arrow
            placement="top"
            title={
              isWarning
                ? 'This agent is active, but there are warnings about its installation that may require your attention'
                : ''
            }
          >
            <Typography
              color={getAgentStatusColor(status, isWarning)}
              variant="inherit"
            >
              {status}
              {isWarning && (
                <Icon
                  css={{ marginLeft: muiTheme.spacing(0.5) }}
                  data-testid="agents-page-list-row-warning-status-icon"
                  icon="solidWarning"
                />
              )}
            </Typography>
          </Tooltip>
        )
      }
    },
    {
      ...defaultColumnProps,
      field: 'id',
      headerName: 'Identifier',
      flex: 3.5,
      renderCell: ({ row }) => {
        return (
          <Link
            data-testid={`agent-list-row-identifier-${row.id}`}
            onClick={() => handleOpen(row)}
          >
            <TruncatedTextWithTooltip
              tooltipProps={{ title: row.id }}
              variant="inherit"
            >
              {row.id}
            </TruncatedTextWithTooltip>
          </Link>
        )
      }
    },
    {
      ...defaultColumnProps,
      field: 'type',
      headerName: 'Agent Type',
      flex: 1.5,
      valueFormatter: ({ value }) =>
        value === AgentType.INFRASTRUCTURE ? 'Infrastructure' : 'Failure Flag'
    },
    {
      ...defaultColumnProps,
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      renderCell: ({ row }) => {
        if (!hasPrivilegesToEdit(row)) {
          return null
        }

        if (row.status === AgentStatus.DEACTIVATED) {
          return <ReactivateAgentButton agent={row} />
        }

        return <DeactivateAgentButton agent={row} />
      }
    }
  ]

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        PaperProps={{
          sx: {
            padding: 6,
            height: '100%',
            width: 800
          }
        }}
        onClose={handleClose}
      >
        <AgentDetailsDrawerContent isOpen={isOpen} />
      </Drawer>
      <Stack sx={{ rowGap: 4 }}>
        <Stack direction="row" sx={{ columnGap: 4 }}>
          <SearchField
            data-testid="agents-list-search"
            helperText="Search by name or tag"
            InputProps={{ sx: { width: SEARCH_INPUT_FIELD_WIDTH } }}
            label="Search"
            value={searchValue}
            onChange={setSearchValue}
          />
          <AgentsSummaryContainer />
        </Stack>
        <Box data-testid="agents-page-list">
          <DataGrid
            {...defaultGridProps}
            columns={columns}
            columnVisibilityModel={{
              actions: isActionsColumnVisible
            }}
            components={{
              NoRowsOverlay: AgentListEmptyState
            }}
            getRowId={(row) => row.id}
            pageSize={pageSize}
            rows={filteredAgents}
            rowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
            sx={getGridStyles()}
            onPageSizeChange={handlePageSizeChange}
            onRowClick={handleRowClick}
          />
        </Box>
      </Stack>
    </>
  )
}
